import { useEffect, useState } from 'react';
import { Alert, List } from 'reactstrap';
import { useMutation, useQuery } from '@tanstack/react-query';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import { toasty, FormValidated, Input } from '../../common/forms/FormElements';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { BlockBorder } from '../../common/Block';
import { SaveButton } from '../../common/forms/SaveButton';

/**
 * Support tool form: seeds a simulated crew drive (job + timesheet + SIM vehicle) and returns the
 * customer-portal deep link. Available in every environment behind the admin-tool permission,
 * with a standing warning: it creates REAL data, so it must not be run in production.
 *
 * Props:
 * @param {function} onSuccess - Callback after successful submission (e.g., refreshUsageHistory)
 */
export const CrewEtaTestDriveForm = ({ onSuccess }) => {
    const [isValidated, setIsValidated] = useState(false);
    const [lastDrive, setLastDrive] = useState(null);

    const { data: customers = [], error: customersError } = useQuery({
        queryKey: ['customers'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.customers()),
    });

    const { data: dispatchLocations = [], error: dispatchLocationsError } = useQuery({
        queryKey: ['dispatchOffices'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
    });

    const { data: employees, error: employeesError } = useQuery({
        queryKey: ['employees'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.employees()),
    });

    const loadError = customersError || dispatchLocationsError || employeesError;

    useEffect(() => {
        if (loadError) {
            console.error(loadError);
            toasty.error('Unable to load the Crew ETA Test Drive tool.');
        }
    }, [loadError]);

    const startTestDrive = useMutation({
        mutationFn: (request) => httpClient.post(ApiRoutes.adminTool.crewEtaTestDriveStart(), request),
        onSuccess: async (response) => {
            setLastDrive(response?.data ?? response);
            toasty.success('Test drive started. Open the portal link to watch the crew arrive.');

            if (onSuccess) await onSuccess();
        },
        onError: (err) => {
            console.error(err);
            toasty.error('Unable to start the test drive. Check the form inputs.');
        },
    });

    const endTestDrive = useMutation({
        mutationFn: (request) => httpClient.post(ApiRoutes.adminTool.crewEtaTestDriveEnd(), request),
        onSuccess: async () => {
            toasty.success('Test drive ended — the simulated job and timesheet were removed.');
            setLastDrive(null);

            if (onSuccess) await onSuccess();
        },
        onError: (err) => {
            console.error(err);
            toasty.error('Unable to end the test drive.');
        },
    });

    const onSubmit = (values) => {
        const lat = parseFloat(values.meetingLatitude);
        const lng = parseFloat(values.meetingLongitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            toasty.error('Please enter the meeting location latitude and longitude.');
            return;
        }

        startTestDrive.mutate({
            customerCompanyId: values.customerCompanyId,
            dispatchCompanyId: values.dispatchCompanyId,
            meetingLatitude: lat,
            meetingLongitude: lng,
            meetingAddress: values.meetingAddress,
            meetingCity: values.meetingCity,
            meetingState: values.meetingState,
            crewLeadEmployeeId: values.crewLeadEmployeeId ?? null,
            driveDurationMinutes: values.driveDurationMinutes
                ? parseInt(values.driveDurationMinutes, 10)
                : null,
        });
    };

    const onEndDrive = () => {
        if (!lastDrive?.timesheetId) return;

        endTestDrive.mutate({ timesheetId: lastDrive.timesheetId });
    };

    return (
        <FormValidated
            id="crewEtaTestDriveForm"
            name="crewEtaTestDriveForm"
            onSubmit={onSubmit}
            isValidated={isValidated}
            setIsValidated={setIsValidated}
            initialValues={{
                meetingAddress: '',
                meetingCity: '',
                meetingState: '',
                meetingLatitude: '',
                meetingLongitude: '',
                driveDurationMinutes: '',
            }}
        >
            {({ values }) => {
                const isSubmitDisabled = startTestDrive.isPending
                    || !values.customerCompanyId
                    || !values.dispatchCompanyId
                    || !values.meetingLatitude
                    || !values.meetingLongitude;

                return (
                    <>
                        <Alert
                            color="danger"
                            className="mb-3"
                        >
                            <strong>Do not run this tool in a production environment.</strong>
                            {' '}
                            It creates a real job, timesheet, and SIM vehicle in the selected
                            customer&apos;s data — customers would see the demo job in their portal,
                            and it flows into dispatch and billing views until the test drive is
                            ended. Use it on dev/QA environments only.
                        </Alert>

                        <ValidatedSelect
                            id="customerCompanyId"
                            label="Customer"
                            name="customerCompanyId"
                            required
                            options={customers}
                            error="A customer selection is required."
                        />

                        <ValidatedSelect
                            id="dispatchCompanyId"
                            label="Dispatch Office"
                            name="dispatchCompanyId"
                            required
                            options={dispatchLocations}
                            error="A dispatch office selection is required."
                        />

                        <ValidatedSelect
                            id="crewLeadEmployeeId"
                            label="Crew Lead (optional — blank uses the designated sim employee)"
                            name="crewLeadEmployeeId"
                            isClearable
                            options={employees ?? []}
                        />

                        <Input
                            label="Meeting Address (optional, display only)"
                            id="meetingAddress"
                            name="meetingAddress"
                            placeholder="123 Main St"
                        />

                        <div
                            className="d-flex"
                            style={{ gap: '1rem' }}
                        >
                            <div className="flex-fill">
                                <Input
                                    label="City (optional)"
                                    id="meetingCity"
                                    name="meetingCity"
                                />
                            </div>
                            <div className="flex-fill">
                                <Input
                                    label="State (optional)"
                                    id="meetingState"
                                    name="meetingState"
                                />
                            </div>
                        </div>

                        <div
                            className="d-flex"
                            style={{ gap: '1rem' }}
                        >
                            <div className="flex-fill">
                                <Input
                                    label="Meeting Latitude"
                                    id="meetingLatitude"
                                    name="meetingLatitude"
                                    placeholder="43.0481"
                                    required
                                    error="Latitude is required."
                                />
                            </div>
                            <div className="flex-fill">
                                <Input
                                    label="Meeting Longitude"
                                    id="meetingLongitude"
                                    name="meetingLongitude"
                                    placeholder="-76.1474"
                                    required
                                    error="Longitude is required."
                                />
                            </div>
                        </div>

                        <Input
                            label="Drive Duration (minutes — 4 suits a demo, 15 a QA pass; blank uses the configured default)"
                            id="driveDurationMinutes"
                            name="driveDurationMinutes"
                            placeholder="15"
                        />

                        <BlockBorder />

                        <div className="d-flex justify-content-center">
                            <SaveButton
                                saving={startTestDrive.isPending}
                                disabled={isSubmitDisabled}
                                label="Start Test Drive"
                            />
                        </div>

                        {lastDrive && (
                            <Alert
                                color="success"
                                className="mt-3"
                            >
                                <div>
                                    <strong>Test drive running.</strong>
                                    {' '}
                                    Job
                                    {' '}
                                    {lastDrive.jobNumber}
                                    {' '}
                                    · timesheet
                                    {' '}
                                    {lastDrive.timesheetId}
                                    {' '}
                                    · vehicle
                                    {' '}
                                    {lastDrive.vehicleIdentifier}
                                </div>
                                <div className="mt-2">
                                    <a
                                        href={lastDrive.portalLink}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Open the customer portal on this job
                                    </a>
                                    {' '}
                                    (log in as one of the customer&apos;s portal contacts). Re-submit the form to
                                    restart the drive.
                                </div>
                                <div className="mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        disabled={endTestDrive.isPending}
                                        onClick={onEndDrive}
                                    >
                                        {endTestDrive.isPending ? 'Ending…' : 'End test drive'}
                                    </button>
                                </div>
                            </Alert>
                        )}
                    </>
                );
            }}
        </FormValidated>
    );
};

/**
 * Execution-history details for Crew ETA Test Drive runs.
 */
export const CrewEtaTestDriveHistoryDetails = ({ record }) => (
    <List type="unstyled">
        {record.input?.customerCompanyId && (
            <li>
                <strong>Customer company id:</strong>
                {' '}
                {record.input.customerCompanyId}
            </li>
        )}
        {record.output?.jobNumber && (
            <li>
                <strong>Job:</strong>
                {' '}
                {record.output.jobNumber}
            </li>
        )}
        {record.output?.timesheetId && (
            <li>
                <strong>Timesheet:</strong>
                {' '}
                {record.output.timesheetId}
            </li>
        )}
        {record.output?.vehicleIdentifier && (
            <li>
                <strong>Vehicle:</strong>
                {' '}
                {record.output.vehicleIdentifier}
            </li>
        )}
        {record.output?.ended && (
            <li>
                <strong>Action:</strong>
                {' '}
                Test drive ended
            </li>
        )}
    </List>
);

export default CrewEtaTestDriveForm;
